import React, { useState, useEffect, useRef } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { Header } from "../Header/Header";
import signupStyle from "./NewSignup.module.css";

const BE = `${process.env.REACT_APP_SL_BE_BASE_ENV_PART}`;
const AUTH_BASE = `https://auth2.${BE}`;

const APPLICATION_STEPS = [
  "full_name", "email", "company",
  "crm", "restaurant_count", "api_state",
  "final_application",
];

const SETUP_STEPS = ["tenant", "password", "final_setup"];

const CRM_OPTIONS = [
  { value: "petpooja", label: "Petpooja",        hint: "Restaurant POS & CRM" },
  { value: "posist",   label: "Posist",          hint: "Restaurant POS & CRM" },
  { value: "rista",    label: "Rista",           hint: "Restaurant POS & CRM" },
  { value: "custom",   label: "Custom / Other",  hint: "In-house or other vendor" },
];

const COUNT_OPTIONS = [
  { value: "lt_100",    label: "Fewer than 100" },
  { value: "100_500",   label: "100 – 500" },
  { value: "500_2000",  label: "500 – 2000" },
  { value: "2000_plus", label: "2000+" },
];

const API_OPTIONS = [
  { value: "all_three",     label: "All three",          hint: "POS, inventory, and customer data" },
  { value: "pos_inventory", label: "POS + inventory",    hint: "Customer data not yet exposed" },
  { value: "customer_only", label: "Customer data only", hint: "No order or inventory APIs" },
  { value: "exploring",     label: "Just exploring",     hint: "Not ready yet" },
];

const LABELS = {
  full_name:         { title: "What should we call you?",           placeholder: "Your full name" },
  email:             { title: "What's your work email?",            placeholder: "you@company.com" },
  company:           { title: "What's your company called?",        placeholder: "Company name" },
  crm:               { title: "Which CRM / POS do you run?",        placeholder: "" },
  restaurant_count:  { title: "How many restaurants do you serve?", placeholder: "" },
  api_state:         { title: "Which APIs can we connect to?",      placeholder: "" },
  final_application: { title: "Ready to submit?",                   placeholder: "" },

  tenant:            { title: "Pick a workspace ID",                placeholder: "acme-restaurants" },
  password:          { title: "Set a password",                     placeholder: "At least 8 characters" },
  final_setup:       { title: "Almost there",                       placeholder: "" },
};

const slugify = (s) =>
  (s || "").toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");

export const NewSignup = () => {
  const location    = useLocation();
  const history     = useHistory();
  const params      = new URLSearchParams(location.search);
  const intentId    = params.get("intent_id");
  const intentEmail = params.get("email");
  const intentToken = params.get("token");

  const [steps, setSteps]             = useState(intentId ? SETUP_STEPS : APPLICATION_STEPS);
  const [step, setStep]               = useState(0);
  const [direction, setDirection]     = useState("forward");
  const [prefillLoading, setPrefillLoading] = useState(!!intentId);
  const [prefillError, setPrefillError]     = useState(null);
  const [submitting, setSubmitting]   = useState(false);
  const [form, setForm] = useState({
    full_name: "", email: "", company: "", tenant: "",
    crm: "", restaurant_count: "", api_state: "",
    password: "",
  });
  const [terms, setTerms]       = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const fileInput               = useRef(null);

  // ── Prefill for intent flow ─────────────────────────
  useEffect(() => {
    if (!intentId) return;
    (async () => {
      try {
        const res = await fetch(`${AUTH_BASE}/${process.env.REACT_APP_STAGE_ENV}/interest_prefill`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: intentToken, email: intentEmail, intent_id: intentId }),
        });
        const data = await res.json();
        if (data.valid) {
          setForm(f => ({
            ...f,
            full_name:        data.prefill.full_name        || "",
            email:            data.prefill.email            || "",
            company:          data.prefill.company          || "",
            crm:              data.prefill.crm              || "",
            restaurant_count: data.prefill.restaurant_count || "",
            api_state:        data.prefill.api_state        || "",
            tenant:           slugify(data.prefill.company || ""),
          }));
          setSteps(SETUP_STEPS);
          setStep(0);
        } else {
          setPrefillError(data.error || "Invalid or expired link");
        }
      } catch (e) {
        setPrefillError("Could not validate onboarding link");
      } finally {
        setPrefillLoading(false);
      }
    })();
  }, [intentId, intentToken, intentEmail]);

  const next = () => { setDirection("forward"); setStep(s => Math.min(s + 1, steps.length - 1)); };
  const back = () => { setDirection("back");    setStep(s => Math.max(s - 1, 0)); };

  const stepKey = steps[step];

  const canAdvance = () => {
    if (stepKey === "final_application" || stepKey === "final_setup") return terms;
    if (stepKey === "password") return form.password.length >= 8;
    return !!form[stepKey];
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
      fd.append("type",   "tenant_type");
      fd.append("role",   "196608");
      fd.append("gender", "");
      fd.append("phone",  "");
      if (logoFile) fd.append("file", logoFile, logoFile.name);
      if (intentId) fd.append("intent_id", intentId);
      if (intentId && form.password) fd.append("password", form.password);

      fd.append("meta_data", JSON.stringify({
        signup_qualification: {
          crm:              form.crm,
          restaurant_count: form.restaurant_count,
          api_state:        form.api_state,
        },
      }));

      const res  = await fetch(`${AUTH_BASE}/${process.env.REACT_APP_STAGE_ENV}/signup`, {
        method: "POST",
        body:   fd,
      });
      const data = await res.json();

      if (data.statusCode && Number(data.statusCode) !== 200) {
        let msg = "Signup failed";
        try { msg = JSON.parse(data.body).error || msg; } catch {}
        alert(msg);
      } else {
        history.push(intentId ? "/onboarding_welcome" : "/signup_pending");
      }
    } catch (e) {
      alert("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (prefillLoading) {
    return (
      <div className={signupStyle.wrapper}>
        <Header />
        <div className={signupStyle.loadingCenter}>Checking your link…</div>
      </div>
    );
  }

  if (prefillError) {
    return (
      <div className={signupStyle.wrapper}>
        <Header />
        <div className={signupStyle.errorCard}>
          <h2>This link isn't valid</h2>
          <p>{prefillError}</p>
          <p className={signupStyle.muted}>
            Email us at <a href="mailto:founder@sageion.com">founder@sageion.com</a> and we'll send a fresh one.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={signupStyle.wrapper}>
      <Header />

      <div className={signupStyle.progressTrack}>
        <div
          className={signupStyle.progressFill}
          style={{ width: `${((step + 1) / steps.length) * 100}%` }}
        />
      </div>

      <div className={signupStyle.stage}>
        <div
          key={step}
          className={[
            signupStyle.screen,
            direction === "forward" ? signupStyle.slideFromRight : signupStyle.slideFromLeft,
          ].join(" ")}
        >
          <h1 className={signupStyle.stepTitle}>{LABELS[stepKey].title}</h1>

          {["full_name", "email", "company", "tenant", "password"].includes(stepKey) && (
            <input
              autoFocus
              type={stepKey === "password" ? "password" : stepKey === "email" ? "email" : "text"}
              className={signupStyle.bigInput}
              placeholder={LABELS[stepKey].placeholder}
              value={form[stepKey]}
              onChange={e => {
                const v = stepKey === "tenant" ? slugify(e.target.value) : e.target.value;
                setForm(f => ({ ...f, [stepKey]: v }));
              }}
              onKeyDown={e => {
                if (e.key === "Enter" && canAdvance()) {
                  step === steps.length - 1 ? handleSubmit() : next();
                }
              }}
            />
          )}

          {["crm", "restaurant_count", "api_state"].includes(stepKey) && (
            <div className={signupStyle.choiceGrid}>
              {(stepKey === "crm" ? CRM_OPTIONS
                : stepKey === "restaurant_count" ? COUNT_OPTIONS
                : API_OPTIONS).map(opt => (
                <button
                  key={opt.value}
                  className={[
                    signupStyle.choiceCard,
                    form[stepKey] === opt.value ? signupStyle.choiceCardActive : "",
                  ].join(" ")}
                  onClick={() => {
                    setForm(f => ({ ...f, [stepKey]: opt.value }));
                    setTimeout(next, 160);
                  }}
                >
                  <div className={signupStyle.choiceTitle}>{opt.label}</div>
                  {opt.hint && <div className={signupStyle.choiceHint}>{opt.hint}</div>}
                </button>
              ))}
            </div>
          )}

          {stepKey === "final_application" && (
            <>
              <p style={{ color: "#4a4a44", marginBottom: "24px" }}>
                By submitting this, our team will review your application. If approved, you'll receive a link to set up your workspace.
              </p>
              <label className={signupStyle.termsRow}>
                <input type="checkbox" checked={terms} onChange={e => setTerms(e.target.checked)} />
                <span>
                  I agree to Sageion <a href="/terms_and_conditions_policy">Terms of Service</a> and <a href="/privacy_policy">Privacy Policy</a>.
                </span>
              </label>
            </>
          )}

          {stepKey === "final_setup" && (
            <>
              <div className={signupStyle.finalBlock}>
                <label className={signupStyle.uploadLabel}>
                  <div className={signupStyle.uploadTitle}>
                    {logoFile ? logoFile.name : "Upload a workspace logo (optional)"}
                  </div>
                  <div className={signupStyle.uploadHint}>PNG / JPG / SVG, up to 5 MB</div>
                  <input
                    ref={fileInput}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={e => setLogoFile(e.target.files?.[0] || null)}
                  />
                </label>
              </div>
              <label className={signupStyle.termsRow}>
                <input type="checkbox" checked={terms} onChange={e => setTerms(e.target.checked)} />
                <span>
                  I agree to Sageion <a href="/terms_and_conditions_policy">Terms of Service</a> and <a href="/privacy_policy">Privacy Policy</a>.
                </span>
              </label>
            </>
          )}

          <div className={signupStyle.navRow}>
            {step > 0 && (
              <button className={signupStyle.backBtn} onClick={back} type="button">← Back</button>
            )}
            <button
              className={signupStyle.nextBtn}
              disabled={!canAdvance() || submitting}
              onClick={() => (step === steps.length - 1 ? handleSubmit() : next())}
              type="button"
            >
              {step === steps.length - 1
                ? (submitting ? "Submitting…" : stepKey === "final_application" ? "Submit Application" : "Create Account")
                : "Continue →"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};